import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getPermissions } from '../../lib/accessControl';
import { roles } from '../../utils/roles';
import { abortController } from '../../utils/abortController';
import withAuth from '../../lib/withAuth';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import usePageFilterPersistence from '../../hooks/usePageFilterPersistence';
import MaterialReactDataTable from '../../components/MaterialReactDataTable';
import Alert from '../../components/Alert';
import Backdrop from '@mui/material/Backdrop';
import Loader from '../../components/Loader';
import axios from 'axios';

const CounterPartyList = ({ logOut = () => {} }) => 
{
    const [isLoading, setIsLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("");
    const navigate = useNavigate();
    const { pathname } = useLocation();

    /* AUTHENTICATION VARIABLES */
    const currentUser = useUser();

    /* AUTHORIZATION VARIABLES */
    const { isWritable = false } = getPermissions(currentUser?.role, pathname);

    /* SESSION EXPIRY VARIABLES */
    const triggerSessionExpire = useSessionExpire();

    /* PERSISTED FILTERS (redux, keyed by route) */
    // Shared explicitly with MaterialReactDataTable (via the pageKey prop below)
    // so both this page and the table read/write the same persisted slot for
    // the global search value.
    const pageKey = `${pathname}::CounterPartyList`;
    const {
        filterValues: persistedFilters,
        setFilter: persistFilter
    } = usePageFilterPersistence(pageKey);
    const hasHydratedGlobalFilterRef = useRef(false);
    const persistDebounceRef = useRef(null);

    /* DATATABLE VARIABLES */
    const [counterPartyListData, setCounterPartyListData] = useState({});
    const [toggleDisplayData, setToggleDisplayData] = useState(false);
    const tableRef = useRef();

    /* DATATABLE GLOBAL FILTER VARIABLES */
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    
    useEffect(() =>
    {
        const fetchData = async () => 
        {
            setIsLoading(true);
            setShowAlert(false);
            setAlertMessage("");
            setAlertType("");

            await axios({
                method: "post",
                url: "/api/GetGRCCounterPartyList",
                data: {
                    filter_value: globalFilterValue
                },
                signal: abortController.signal
            })
            .then((response) => 
            {
                setIsLoading(false);
                const { status, data } = response;
        
                if (status === 200) 
                {
                    const counterPartyListData = data?.counter_party_list_data || {};      
                    setCounterPartyListData(counterPartyListData);
                }
                else
                {
                    setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                    setAlertType("error");
                    setShowAlert(true);
                }
            })
            .catch((error) => 
            {
                console.log("Get Counter Party List Data Api: ", error);
                setIsLoading(false);

                if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
                {
                    return;
                }
                
                const status = error?.response?.status;

                if (status === 403) 
                {
                    triggerSessionExpire();
                } 
                else 
                {
                    setAlertMessage(
                        status === 401
                            ? "Unauthorized access. You do not have the required permissions to perform this action."
                            : status === 429
                            ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                            : "An error occurred while processing your request. Please try again later or contact the site administrator."
                    );
                    setAlertType("error");
                    setShowAlert(true);
                
                    if (status === 429) 
                    {
                        setTimeout(logOut, 3000);
                    }
                }
            });
        }
    
        fetchData();

        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [toggleDisplayData]);

    /* -------- PERSISTED GLOBAL SEARCH: hydrate once on mount -------- */
    // redux-persist rehydrates the store before this component renders, so
    // persistedFilters.globalFilterValue is already available on first mount.
    // Since the fetch effect above only re-runs on toggleDisplayData changes
    // (not on globalFilterValue), we nudge toggleDisplayData here so a
    // persisted search value is actually applied to the initial fetch.
    useEffect(() => {
        if (hasHydratedGlobalFilterRef.current) return;
        hasHydratedGlobalFilterRef.current = true;

        const persistedValue = persistedFilters?.globalFilterValue;

        if (persistedValue) {
            setGlobalFilterValue(persistedValue);
            setToggleDisplayData((previous) => !previous);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    /* -------- PERSISTED GLOBAL SEARCH: persist on change (debounced) -------- */
    useEffect(() => {
        if (!hasHydratedGlobalFilterRef.current) return;

        if (persistDebounceRef.current) {
            clearTimeout(persistDebounceRef.current);
        }

        persistDebounceRef.current = setTimeout(() => {
            persistFilter('globalFilterValue', globalFilterValue || "");
        }, 400);

        return () => clearTimeout(persistDebounceRef.current);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [globalFilterValue]);

    const handleGlobalFilter = async (rowData) =>
    {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");
        
        await axios({
            method: "post",
            url: "/api/FilterTableData",
            data: { 
                pathname: pathname,
                table_data: rowData,
                filter_value: globalFilterValue
            },
            signal: abortController.signal
        })
        .then((response) => 
        {
            setIsLoading(false);
            const { status, data } = response;
    
            if (status === 200) 
            {
                tableRef?.current?.setFilteredData(data || []);
            }
            else
            {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
            }
        })
        .catch((error) => 
        {
            console.log("Filter Table Data Api: ", error);
            setIsLoading(false);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
            {
                return;
            }
            
            const status = error?.response?.status;

            if (status === 403) 
            {
                triggerSessionExpire();
            } 
            else 
            {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access. You do not have the required permissions to perform this action."
                        : status === 429
                        ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                        : "An error occurred while processing your request. Please try again later or contact the site administrator."
                );
                setAlertType("error");
                setShowAlert(true);
            
                if (status === 429) 
                {
                    setTimeout(logOut, 3000);
                }
            }
        });        
    }

    const handleEditKYCRequest = async (id) =>
    {
        setIsLoading(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");
        
        await axios({
            method: "post",
            url: "/api/SetKYCRequestFormLock",
            data: {
                uuid: id,
                lock_kyc: true
            },
            signal: abortController.signal
        })
        .then((response) => 
        {
            setIsLoading(false);
            const { status } = response;
    
            if (status === 200) 
            {
                navigate("/kyc/edit-kyc", { state: { isCounterPartyListItem: true, kycId: id, previousPage: "Counter Party List" } });
            }
            else
            {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
            }
        })
        .catch((error) => 
        {
            console.log("Set KYC Request Form Lock Api: ", error);
            setIsLoading(false);
            
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") 
            {
                return;
            }

            const status = error?.response?.status;

            if (status === 403) 
            {
                triggerSessionExpire();
            } 
            else 
            {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access. You do not have the required permissions to perform this action."
                        : status === 429
                        ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                        : "An error occurred while processing your request. Please try again later or contact the site administrator."
                );
                setAlertType("error");
                setShowAlert(true);
            
                if (status === 429) 
                {
                    setTimeout(logOut, 3000);
                }
            }
        });
    }
        
    return (
        <div className = "content-container d-flex flex-column container kyc">
            <Alert 
                show = {showAlert}
                message = {alertMessage}
                type = {alertType}
                setShow = {setShowAlert}
            />

            <Backdrop
                sx = {{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open = {isLoading}
            >
                <Loader/>
            </Backdrop>

            <h4 className = "page-heading m-0">
               Counter Party List
            </h4>

            <LocalizationProvider dateAdapter = {AdapterDayjs}>
                <MaterialReactDataTable
                    ref = {tableRef}
                    title = "Counter Party List"
                    pageKey = {pageKey}
                    isWritable = {isWritable}
                    isLoading = {isLoading}
                    isBusinessUnitSelected = {false}
                    currentUserId = {currentUser?.id}
                    globalFilterValue = {globalFilterValue}
                    tableData = {counterPartyListData}
                    setIsLoading = {setIsLoading}
                    setShowAlert = {setShowAlert}
                    setAlertMessage = {setAlertMessage}
                    setAlertType = {setAlertType}
                    setGlobalFilterValue = {setGlobalFilterValue}
                    handleGlobalFilter = {handleGlobalFilter}
                    handleEditKYCRequest = {handleEditKYCRequest}
                />
            </LocalizationProvider>
        </div>
    );
}

export default withAuth(CounterPartyList)([
    roles[7],
    roles[19]
]);